import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, from, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { MicrosoftGraphService } from '../../mail/services/microsoft-graph.service';
import {
  OutlookCalendarConsent,
  OutlookConnectionState,
  OutlookError
} from '../models/IOutlookPanel.interface';
import {
  IOutlookAttachment,
  IOutlookFolderStats,
  IOutlookMessageDetail,
  IOutlookMessageListItem,
  IOutlookRecipient,
  OutlookWellKnownFolder
} from '../models/IOutlookMessage.interface';
import { IOutlookContact } from '../models/IOutlookContact.interface';
import { IOutlookEvent } from '../models/IOutlookEvent.interface';
import {
  IOutlookAccountSummary,
  IOutlookMailboxSettings,
  IOutlookProfile
} from '../models/IOutlookProfile.interface';
import { IOutlookSignatureCandidate } from '../models/IOutlookSignature.interface';
import { buildDerivedSignatureHtml, extractSignatureFromHtml } from '../utils/signature-extract.util';

/**
 * Everything the Outlook panel knows about Microsoft 365.
 *
 * This is the only consumer of MicrosoftGraphService in the feature, and it exists to hold three
 * things the components should not each reinvent: the not-connected guard, the per-key cache, and
 * one consistent translation of Graph failures into something a user can act on.
 *
 * Note on company scope: none of this data is company-scoped. It belongs to the signed-in person,
 * so switching the managed company in BizCombo changes nothing here and the cache is deliberately
 * not keyed on it. The one exception is saving a signature into BizCombo, which does target the
 * current company — and that lives in the signature tab, where it can be labelled as such.
 */
@Injectable({ providedIn: 'root' })
export class OutlookDataService implements OnDestroy {
  private readonly graph = inject(MicrosoftGraphService);

  // ─── Connection ───
  private connectionSubject = new BehaviorSubject<OutlookConnectionState>('unknown');
  readonly connection$: Observable<OutlookConnectionState> = this.connectionSubject.asObservable();

  private calendarConsentSubject = new BehaviorSubject<OutlookCalendarConsent>('unknown');
  readonly calendarConsent$: Observable<OutlookCalendarConsent> = this.calendarConsentSubject.asObservable();

  /**
   * Cached streams, keyed by what they hold: 'profile', 'mailboxSettings', 'contacts',
   * 'mail:{folder}', 'calendar:{startIso}', 'signature'.
   *
   * Tabs are created and destroyed by *ngIf as the user switches between them, so without this
   * every trip back to Mail would re-hit Graph. refCount stays false precisely because the
   * subscriber count drops to zero on every tab switch — a refCounted shareReplay would discard the
   * buffer at exactly the wrong moment.
   */
  private cache = new Map<string, Observable<any>>();

  /** Object URLs handed out for photo blobs, tracked so they can be revoked rather than leaked. */
  private objectUrls = new Map<string, string>();

  // ═══ Connection ═══

  /**
   * Re-reads whether Graph is reachable. Synchronous underneath and never prompts, so it is safe to
   * call on panel open.
   */
  refreshConnection(): void {
    this.connectionSubject.next(this.graph.isConnected() ? 'connected' : 'disconnected');
  }

  /** Interactive sign-in. Call from a click — MSAL popups outside a user gesture get blocked. */
  connect(): Observable<boolean> {
    return from(this.graph.login()).pipe(
      map(() => {
        this.refreshConnection();
        return this.graph.isConnected();
      }),
      catchError((err) => {
        // A user closing the popup is not an error worth shouting about; anything else is.
        if (this.isUserCancellation(err)) {
          this.refreshConnection();
        } else {
          this.connectionSubject.next('error');
        }
        return of(false);
      })
    );
  }

  /** Clears the MSAL cache — the escape hatch for a wedged auth state. */
  disconnect(): Observable<void> {
    return from(this.graph.clearCache()).pipe(
      tap(() => {
        this.invalidate();
        this.connectionSubject.next('disconnected');
        this.calendarConsentSubject.next('unknown');
      }),
      catchError(() => of(void 0))
    );
  }

  /**
   * Silent, promptless reconnect attempt for the browser-restart case, where sessionStorage was
   * cleared but the Microsoft session cookie survives. Never prompts, never throws.
   */
  trySilentSignIn(loginHint: string): Observable<boolean> {
    if (!loginHint) {
      return of(false);
    }
    return from(this.graph.trySilentSignIn(loginHint)).pipe(
      tap((ok) => {
        if (ok) {
          this.refreshConnection();
        }
      }),
      catchError(() => of(false))
    );
  }

  isPersonalAccount(): boolean {
    return this.graph.isPersonalAccount();
  }

  accountSummary(): IOutlookAccountSummary | null {
    return this.graph.getAccountSummary();
  }

  // ═══ Mail ═══

  getMessages(folder: OutlookWellKnownFolder = 'inbox', force = false): Observable<IOutlookMessageListItem[]> {
    return this.cached(`mail:${folder}`, force, [], () =>
      from(
        this.graph.getMails({
          folder,
          top: 30,
          orderby: 'receivedDateTime desc',
          // Explicitly excludes `body`. The method's own default includes it, which would pull
          // thirty full HTML bodies down to render a list that only shows the preview line.
          select: 'id,subject,bodyPreview,from,receivedDateTime,hasAttachments,isRead,flag,importance'
        })
      ).pipe(map((res: any) => (res?.value ?? []).map((m: any) => this.toListItem(m))))
    );
  }

  /**
   * Server-side search across the mailbox.
   *
   * Not cached: the query changes on every keystroke-debounce, so a cache would only ever grow.
   * Graph forbids $search together with $orderby, hence results come back in relevance order.
   */
  searchMessages(query: string): Observable<IOutlookMessageListItem[]> {
    if (!this.guard()) {
      return of([]);
    }
    return from(
      this.graph.searchMails(query, {
        top: 25,
        select: 'id,subject,bodyPreview,from,receivedDateTime,hasAttachments,isRead,flag,importance'
      })
    ).pipe(
      map((res: any) => (res?.value ?? []).map((m: any) => this.toListItem(m))),
      catchError(() => of([]))
    );
  }

  getMessage(id: string): Observable<IOutlookMessageDetail | null> {
    if (!this.guard()) {
      return of(null);
    }
    return from(this.graph.getMail(id)).pipe(
      map((m: any) => (m ? this.toDetail(m) : null)),
      catchError(() => of(null))
    );
  }

  getFolderStats(folder: OutlookWellKnownFolder): Observable<IOutlookFolderStats | null> {
    if (!this.guard()) {
      return of(null);
    }
    return from(this.graph.getMailFolderStats(folder)).pipe(
      map((f: any) =>
        f
          ? { displayName: f.displayName ?? folder, total: f.totalItemCount ?? 0, unread: f.unreadItemCount ?? 0 }
          : null
      ),
      catchError(() => of(null))
    );
  }

  /**
   * Deeplink to a message in Outlook Web.
   *
   * The panel holds read-only scopes, so every write — reply, forward, delete — is a hand-off to
   * OWA rather than something it attempts and fails at.
   */
  owaMessageUrl(id: string): string {
    return `https://outlook.office.com/owa/?ItemID=${encodeURIComponent(id)}&exvsurl=1&viewmodel=ReadMessageItem`;
  }

  owaComposeUrl(to?: string, subject?: string): string {
    const params = new URLSearchParams({ path: '/mail/action/compose' });
    if (to) {
      params.set('to', to);
    }
    if (subject) {
      params.set('subject', subject);
    }
    return `https://outlook.office.com/mail/deeplink/compose?${params.toString()}`;
  }

  // ═══ Calendar ═══

  /** Silent consent probe. Never prompts — see hasCalendarConsent in the Graph service. */
  checkCalendarConsent(): void {
    if (!this.graph.isConnected()) {
      this.calendarConsentSubject.next('missing');
      return;
    }
    from(this.graph.hasCalendarConsent())
      .pipe(catchError(() => of(false)))
      .subscribe((granted) => this.calendarConsentSubject.next(granted ? 'granted' : 'missing'));
  }

  /** Interactive consent. Click-only. */
  requestCalendarConsent(): Observable<boolean> {
    return from(this.graph.requestCalendarConsent()).pipe(
      map(() => {
        this.calendarConsentSubject.next('granted');
        this.invalidate('calendar');
        return true;
      }),
      catchError((err) => {
        if (this.isAdminConsentRequired(err)) {
          // Asking again cannot help — only an administrator can grant this.
          this.calendarConsentSubject.next('blocked');
        } else {
          this.calendarConsentSubject.next('missing');
        }
        return of(false);
      })
    );
  }

  /**
   * The next `days` of calendar occurrences, ordered by start.
   *
   * Cached on the window start so switching tabs does not refetch, but the key changes at midnight,
   * which is the one time a stale agenda would actually be wrong.
   */
  getAgenda(days = 7, force = false): Observable<IOutlookEvent[]> {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + days);

    return this.cached(`calendar:${start.toISOString()}:${days}`, force, [], () =>
      // The mailbox time zone goes out with the request so Graph returns already-localised times
      // rather than leaving the client to convert and get DST wrong. Its absence is not fatal.
      this.getMailboxSettings().pipe(
        map((settings) => settings?.timeZone ?? null),
        catchError(() => of(null)),
        switchMap((tz) =>
          from(this.graph.getCalendarView(start.toISOString(), end.toISOString(), 100, tz)).pipe(
            map((events: any[]) => events.map((e) => this.toEvent(e)))
          )
        )
      )
    );
  }

  // ═══ Contacts ═══

  getContacts(force = false): Observable<IOutlookContact[]> {
    return this.cached('contacts', force, [], () =>
      from(this.graph.getOutlookContacts(500)).pipe(map((list) => (list ?? []).map((c) => this.toContact(c))))
    );
  }

  /**
   * Photo for one contact, as an object URL.
   *
   * Resolved per row rather than for the whole list because Graph serves exactly one photo per
   * request — fetching five hundred up front would be five hundred round trips for a list where
   * most rows are never scrolled to.
   */
  getContactPhoto(contactId: string): Observable<string | null> {
    const existing = this.objectUrls.get(contactId);
    if (existing) {
      return of(existing);
    }
    if (!this.guard()) {
      return of(null);
    }
    return from(this.graph.getOutlookContactPhoto(contactId)).pipe(
      map((blob) => {
        if (!blob) {
          return null;
        }
        const url = URL.createObjectURL(blob);
        this.objectUrls.set(contactId, url);
        return url;
      }),
      catchError(() => of(null))
    );
  }

  // ═══ Profile ═══

  getProfile(force = false): Observable<IOutlookProfile | null> {
    return this.cached('profile', force, null, () =>
      from(this.graph.getUserProfileForSignature()).pipe(
        map((me: any) => (me ? this.toProfile(me) : null)),
        switchMap((profile) => {
          if (!profile) {
            return of(null);
          }
          // The photo is a separate request that legitimately 404s for anyone who has not set one,
          // so its failure must not take the whole profile down with it.
          return from(this.graph.getUserPhoto()).pipe(
            catchError(() => of(null as Blob | null)),
            map((blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob);
                this.objectUrls.set('me:photo', url);
                profile.photoUrl = url;
              }
              return profile;
            })
          );
        })
      )
    );
  }

  getMailboxSettings(force = false): Observable<IOutlookMailboxSettings | null> {
    // Personal accounts have no such resource; skip the request rather than let it 400.
    if (this.graph.isPersonalAccount()) {
      return of(null);
    }
    return this.cached('mailboxSettings', force, null, () =>
      from(this.graph.getMailboxSettings()).pipe(map((s: any) => (s ? this.toMailboxSettings(s) : null)))
    );
  }

  // ═══ Signature ═══

  /**
   * Signature candidates, best first.
   *
   * Graph does not expose the real Outlook signature — see the header comment in
   * signature-extract.util.ts. These are honest reconstructions, each labelled with where it came
   * from, and the derived one is always present because it needs nothing but the profile.
   */
  getSignatureCandidates(force = false): Observable<IOutlookSignatureCandidate[]> {
    return this.cached('signature', force, [], () =>
      forkJoin({
        profile: this.getProfile().pipe(catchError(() => of(null))),
        lastSent: from(this.graph.getLastSentMessage()).pipe(catchError(() => of(null)))
      }).pipe(
        map(({ profile, lastSent }) => {
          const candidates: IOutlookSignatureCandidate[] = [];

          if (profile) {
            candidates.push({
              source: 'derived',
              label: 'Built from your Microsoft 365 profile',
              hint: 'Assembled from your directory details. Always available, and safe to edit below.',
              html: buildDerivedSignatureHtml(profile),
              confidence: 'high'
            });
          }

          const body = lastSent?.body?.content;
          if (body) {
            const found = extractSignatureFromHtml(body);
            if (found) {
              candidates.push({
                source: 'detected',
                label: 'Detected from your most recent sent message',
                hint: 'Found by inspecting your last sent email — check it before you use it.',
                html: found.html,
                confidence: found.confidence
              });
            }
          }

          return candidates;
        })
      )
    );
  }

  // ═══ Cache ═══

  /** Drops cached streams. Pass a prefix to clear one area, nothing to clear everything. */
  invalidate(prefix?: string): void {
    if (!prefix) {
      this.cache.clear();
      this.releaseObjectUrls();
      return;
    }
    Array.from(this.cache.keys())
      .filter((k) => k === prefix || k.startsWith(`${prefix}:`))
      .forEach((k) => this.cache.delete(k));
  }

  ngOnDestroy(): void {
    this.releaseObjectUrls();
  }

  // ═══ Internals ═══

  /**
   * The single most important rule in this file: never issue a Graph call while disconnected.
   *
   * getHeaders() leads to acquireToken(), which on a missing or expired token escalates to an
   * interactive popup. Reached from a tab's ngOnInit rather than from a click, that popup is not
   * attached to a user gesture and the browser blocks it — leaving a panel that silently does
   * nothing. Every data method checks this first and returns an empty result instead.
   */
  private guard(): boolean {
    return this.graph.isConnected();
  }

  /**
   * @param empty what to emit when disconnected or on failure — `[]` for lists, `null` for
   *              singletons. Passed in rather than inferred so a list caller never receives a null
   *              it would have to guard against in a template.
   */
  private cached<T>(key: string, force: boolean, empty: T, factory: () => Observable<T>): Observable<T> {
    if (!this.guard()) {
      return of(empty);
    }
    if (force) {
      this.cache.delete(key);
    }
    const hit = this.cache.get(key);
    if (hit) {
      return hit as Observable<T>;
    }

    const stream = factory().pipe(
      catchError((err) => {
        // A failed request must not stay cached, or a transient blip becomes permanent for the
        // whole session. Dropping the key means the next subscription genuinely retries.
        this.cache.delete(key);
        const normalised = this.normalise(err);
        if (normalised.action === 'reconnect') {
          this.connectionSubject.next('error');
        }
        return of(empty);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.cache.set(key, stream);
    return stream;
  }

  /** Turns a Graph or MSAL failure into something the connect-state component can render. */
  normalise(err: any): OutlookError {
    const status = err?.status;
    const code = err?.errorCode ?? '';
    const message = String(err?.message ?? '');

    if (this.isUserCancellation(err)) {
      return { state: 'connected', message: '', action: 'retry' };
    }
    if (code.includes('popup_window_error') || code.includes('empty_window_error') || message.includes('popup')) {
      return {
        state: 'error',
        message: 'Your browser blocked the sign-in window.',
        action: 'redirect'
      };
    }
    if (this.isAdminConsentRequired(err)) {
      return {
        state: 'error',
        message: 'Your organisation requires an administrator to approve this permission.',
        action: 'retry'
      };
    }
    if (status === 401 || code.includes('interaction_required') || message.includes('InteractionRequired')) {
      return { state: 'error', message: 'Your Outlook connection expired.', action: 'reconnect' };
    }
    if (status === 403) {
      return {
        state: 'error',
        message: "Klidi doesn't have permission to read this from your mailbox.",
        action: 'retry'
      };
    }
    if (status === 429) {
      return { state: 'error', message: 'Outlook is rate-limiting requests. Try again in a moment.', action: 'retry' };
    }
    if (status === 0) {
      return { state: 'error', message: "Can't reach Microsoft 365. Check your connection.", action: 'retry' };
    }
    return { state: 'error', message: 'Something went wrong talking to Outlook.', action: 'retry' };
  }

  private isUserCancellation(err: any): boolean {
    const code = String(err?.errorCode ?? '');
    const message = String(err?.message ?? '');
    return code.includes('user_cancelled') || message.includes('user_cancelled') || message.includes('User cancelled');
  }

  private isAdminConsentRequired(err: any): boolean {
    const text = `${err?.errorCode ?? ''} ${err?.message ?? ''} ${err?.errorMessage ?? ''}`;
    return text.includes('AADSTS65001') || text.includes('consent_required') || text.includes('admin');
  }

  private releaseObjectUrls(): void {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrls.clear();
  }

  // ═══ Graph → view-model mapping ═══

  private toListItem(m: any): IOutlookMessageListItem {
    return {
      id: m.id,
      subject: m.subject || '(no subject)',
      preview: m.bodyPreview || '',
      fromName: m.from?.emailAddress?.name || m.from?.emailAddress?.address || 'Unknown',
      fromAddress: m.from?.emailAddress?.address || '',
      receivedDateTime: m.receivedDateTime,
      isRead: !!m.isRead,
      hasAttachments: !!m.hasAttachments,
      importance: m.importance || 'normal',
      flagged: m.flag?.flagStatus === 'flagged'
    };
  }

  private toDetail(m: any): IOutlookMessageDetail {
    const recipients = (list: any[]): IOutlookRecipient[] =>
      (list ?? []).map((r) => ({
        name: r.emailAddress?.name || r.emailAddress?.address || '',
        address: r.emailAddress?.address || ''
      }));

    const attachments: IOutlookAttachment[] = (m.attachments ?? []).map((a: any) => ({
      id: a.id,
      name: a.name || 'attachment',
      contentType: a.contentType || 'application/octet-stream',
      size: a.size || 0,
      isInline: !!a.isInline
    }));

    return {
      ...this.toListItem(m),
      bodyHtml: m.body?.content || '',
      bodyType: m.body?.contentType === 'text' ? 'text' : 'html',
      to: recipients(m.toRecipients),
      cc: recipients(m.ccRecipients),
      // Inline images are part of the body, not separate files worth listing.
      attachments: attachments.filter((a) => !a.isInline),
      webLink: m.webLink || null
    };
  }

  private toEvent(e: any): IOutlookEvent {
    return {
      id: e.id,
      subject: e.subject || '(no subject)',
      start: e.start?.dateTime || e.start?.date || '',
      end: e.end?.dateTime || e.end?.date || '',
      isAllDay: !!e.isAllDay,
      location: e.location?.displayName || '',
      organizerName: e.organizer?.emailAddress?.name || '',
      attendeeCount: (e.attendees ?? []).length,
      joinUrl: e.onlineMeeting?.joinUrl || e.onlineMeetingUrl || null,
      webLink: e.webLink || null,
      showAs: e.showAs || 'busy',
      isCancelled: !!e.isCancelled
    };
  }

  private toContact(c: any): IOutlookContact {
    const emails: string[] = (c.emailAddresses ?? []).map((e: any) => e.address).filter(Boolean);
    const displayName = c.displayName || [c.givenName, c.surname].filter(Boolean).join(' ') || emails[0] || 'Unknown';

    return {
      id: c.id,
      displayName,
      givenName: c.givenName || null,
      surname: c.surname || null,
      email: emails[0] ?? null,
      otherEmails: emails.slice(1),
      mobilePhone: c.mobilePhone || null,
      businessPhone: (c.businessPhones ?? [])[0] || null,
      companyName: c.companyName || null,
      jobTitle: c.jobTitle || null,
      department: c.department || null,
      photoUrl: null,
      initials: this.initialsOf(displayName)
    };
  }

  private toProfile(me: any): IOutlookProfile {
    return {
      displayName: me.displayName || me.userPrincipalName || 'Unknown',
      givenName: me.givenName || null,
      surname: me.surname || null,
      jobTitle: me.jobTitle || null,
      department: me.department || null,
      companyName: me.companyName || null,
      mail: me.mail || null,
      userPrincipalName: me.userPrincipalName || null,
      mobilePhone: me.mobilePhone || null,
      businessPhones: me.businessPhones ?? [],
      officeLocation: me.officeLocation || null,
      city: me.city || null,
      country: me.country || null,
      streetAddress: me.streetAddress || null,
      postalCode: me.postalCode || null,
      faxNumber: me.faxNumber || null,
      photoUrl: null
    };
  }

  private toMailboxSettings(s: any): IOutlookMailboxSettings {
    const auto = s.automaticRepliesSetting;
    return {
      timeZone: s.timeZone || null,
      language: s.language?.displayName || s.language?.locale || null,
      dateFormat: s.dateFormat || null,
      timeFormat: s.timeFormat || null,
      userPurpose: s.userPurpose || null,
      automaticReplies: auto
        ? {
            status: auto.status || 'disabled',
            internalReplyMessage: auto.internalReplyMessage || '',
            externalReplyMessage: auto.externalReplyMessage || '',
            scheduledStart: auto.scheduledStartDateTime?.dateTime || null,
            scheduledEnd: auto.scheduledEndDateTime?.dateTime || null
          }
        : null,
      workingHours: s.workingHours
        ? {
            daysOfWeek: s.workingHours.daysOfWeek ?? [],
            startTime: s.workingHours.startTime || '',
            endTime: s.workingHours.endTime || '',
            timeZone: s.workingHours.timeZone?.name || null
          }
        : null
    };
  }

  private initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
}
